<template>
    <div v-if="featureInfo" class="v-atlas-raumplanung-feature-info-overview">



        <h3>
            {{ featureInfo.attributeMetadata.label }}, {{ featureInfo.responseMetadata.timeFrameLabel }}
        </h3>

        <div class="v-atlas-raumplanung-feature-info__mainValue">

            <div>
                {{ formatNumber(featureInfo.currentValue, {
                    numDecimals: featureInfo.attributeMetadata.numDecimals ?? 2
                }) }} {{ featureInfo.attributeMetadata.unit }}
            </div>


        </div>



        <!--#region The main indicator chart -->

        <template v-if="option">
            <h3>Historische Entwicklung</h3>
            <div class="v-atlas-raumplanung-feature-info-overview__chart">
                <VChart ref="chartRef" :option="chartOption as ECBasicOption" autoresize
                    @legendselectchanged="handleLegendSelectChanged" />
            </div>
            <EChartsLegend :option="option" :selected="selected" @toggle="handleLegendClick" />
        </template>

        <!--#region Compare values table (for timespan attributes, where no chart is available) -->
        <template v-else-if="featureInfo.compareValues?.length">
            <h3>Vergleichswerte</h3>

            <!--
            <p>
                Hinweis: Für Zeitraum-basierte Indikatoren ist keine Visualisierung der historischen Entwicklung
                verfügbar.
            </p>
-->
            <table class="v-atlas-raumplanung-feature-info-overview__compare-table">
                <thead>
                    <tr>
                        <th>Raumeinheit</th>
                        <th class="v-atlas-raumplanung-feature-info-overview__compare-table-value">
                            Wert

                            <template v-if="featureInfo.attributeMetadata.unit">
                                ({{ featureInfo.attributeMetadata.unit }})
                            </template>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="row in featureInfo.compareValues" :key="row.label"
                        :class="{ 'x-is-main-entity': row.isMainEntity }">
                        <td>{{ row.label }}</td>
                        <td class="v-atlas-raumplanung-feature-info-overview__compare-table-value">
                            {{ formatNumber(row.value, {
                                numDecimals: featureInfo.attributeMetadata.numDecimals ?? 2
                            }) }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </template>
        <!--#endregion Compare values table -->

        <template v-else>
            Hinweis: Für Zeitraum-basierte Indikatoren ist keine Visualisierung der historischen Entwicklung verfügbar.
        </template>

        <!--#endregion The main indicator chart -->






        <!--#region Raumplanerische Funktionszuweisungen-->

        <div v-if="featureInfo?.entity?._tags.includes('type:Gemeinde')"
            class="v-atlas-raumplanung-feature-info__funktionszuweisungen">
            <h3>Raumplanerische Funktionszuweisungen</h3>
            <h4>Raumkategorie:</h4>
            {{ featureInfo?.entity?.raumkategorie?.[0]?.value }}

            <h4>Zentralörtlichkeit:</h4>
            {{ featureInfo?.entity?.zentraloertlichkeit?.[0]?.value }}
            <h4>Wohnfunktion:</h4>
            {{ featureInfo?.entity?.wohnfunktion?.[0]?.value }}
            <h4>Gewerbefunktion:</h4>
            {{ featureInfo?.entity?.gewerbefunktion?.[0]?.value }}
        </div>

        <!--#endregion Raumplanerische Funktionszuweisungen-->
    </div>



</template>

<script setup lang="ts">

import { RaumplanungFeatureInfoResponse } from "atlas-shared";
import { formatNumber } from "gertrud";
import VChart from 'vue-echarts';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';

import {
    TitleComponent,
    TooltipComponent,
    LegendComponent,
    GridComponent,
} from 'echarts/components';

import { LineChart } from 'echarts/charts';

import { computed, ref } from "vue";
import { ECBasicOption } from "echarts/types/dist/shared";
import { RaumplanungMapModule } from "./RaumplanungMapModule";
import EChartsLegend from "./EChartsLegend.vue";


const props = defineProps<{
    featureInfo: RaumplanungFeatureInfoResponse
    mapModule: RaumplanungMapModule
}>()


use([
    CanvasRenderer,
    TitleComponent,
    TooltipComponent,
    LegendComponent,
    GridComponent,
    LineChart,
]);



const option = computed(() => {
    return props.featureInfo.eChartsOption
})

//#region Chart option & legend

// The echarts legend can have a variable number of entries with variable
// label lengths, which makes it hard to size the canvas reliably. Instead of
// rendering the legend inside the echarts canvas (where it can be clipped or
// overlap the plot), we hide the echarts legend and render it as a normal
// HTML element below the chart. The chart height then only needs to fit the
// plot area, and the legend can never be clipped or overlap.
//
// The HTML legend is clickable: clicking an item toggles the corresponding
// series via echarts' `legendToggleSelect` action, and the selected state is
// kept in sync through the `legendselectchanged` event.


const chartRef = ref<InstanceType<typeof VChart> | null>(null)

// Formats numbers according to German conventions (decimal comma, dot
// thousands separator). Used for the y-axis labels and the tooltip of the
// chart. This must run on the client: the chart option is transported from
// the server as JSON, and JSON.stringify silently drops function properties,
// so formatter functions cannot be part of the server response.
function formatChartNumber(value: number | null | undefined): string {
    if (value === null || value === undefined || isNaN(Number(value))) return "-"
    const numDecimals = props.featureInfo.attributeMetadata.numDecimals ?? 2
    return new Intl.NumberFormat('de-DE', {
        minimumFractionDigits: numDecimals,
        maximumFractionDigits: numDecimals
    }).format(Number(value))
}

const chartOption = computed(() => {
    const opt = option.value
    if (!opt) return undefined

    // Hide the echarts legend; it is rendered as HTML below the chart.
    return {
        ...opt,
        legend: {
            ...(opt.legend ?? {}),
            show: false
        },
        // Format the y-axis tick labels in German number format.
        yAxis: {
            ...(opt.yAxis ?? {}),
            axisLabel: {
                ...(opt.yAxis?.axisLabel ?? {}),
                formatter: (value: number) => formatChartNumber(value)
            }
        },
        // Show a tooltip with the value of the hovered data point,
        // including the year and the name of the spatial unit.
        tooltip: {
            ...(opt.tooltip ?? {}),
            trigger: "item",
            axisPointer: undefined,
            // Force a maximum tooltip width of 200px and let the spatial
            // unit label wrap within that width. echarts sets
            // `white-space: nowrap` on the tooltip content by default,
            // so it must be overridden here to allow wrapping.
            extraCssText: "max-width: 200px; white-space: normal; word-break: break-word;",
            formatter: (params: any) => {
                const value = params?.value
                const year = params?.name ?? ""
                const spatialUnit = params?.seriesName ?? ""
                return `${spatialUnit}, ${year}<br/><b>${formatChartNumber(value)} ${props.featureInfo.attributeMetadata.unit ?? ""}</b>`
            }
        }
    }
})

// Build the HTML legend items from the echarts legend data + series colors.
// `selected` is a reactive map (keyed by legend name) that tracks whether the
// series is currently shown in the chart. It is kept in a separate `ref` so
// that toggling it triggers a re-render of the legend.
const selected = ref<Record<string, boolean>>({})

function handleLegendClick(name: string) {
    // Toggle the series visibility in the echarts instance.
    // We manage the selected state optimistically here (instead of relying
    // solely on the `legendselectchanged` event) so that a series can be
    // re-selected even if it has no data. echarts does not reliably fire the
    // event for empty series, which previously made it impossible to re-select
    // them after deselecting.
    const isCurrentlySelected = selected.value[name] ?? true
    selected.value[name] = !isCurrentlySelected
    chartRef.value?.dispatchAction({
        type: isCurrentlySelected ? "legendUnSelect" : "legendSelect",
        name
    })
}

function handleLegendSelectChanged(params: any) {
    // Sync the HTML legend's selected state with the echarts legend state.
    const newSelected = params?.selected ?? {}
    for (const name of Object.keys(newSelected)) {
        selected.value[name] = newSelected[name]
    }
}

//#endregion Chart option & legend

</script>

<style scoped>
.v-atlas-raumplanung-feature-info-overview__chart {
    width: 100%;
    height: 300px;
    margin: 20px 0;
}

.v-atlas-raumplanung-feature-info-overview__compare-table {
    width: 100%;
    border-collapse: collapse;
    margin: 20px 0;
    font-size: 14px;
}

.v-atlas-raumplanung-feature-info-overview__compare-table th,
.v-atlas-raumplanung-feature-info-overview__compare-table td {
    padding: 8px 12px;
    border-bottom: 1px solid var(--v-color-border, #ddd);
    text-align: left;
}

.v-atlas-raumplanung-feature-info-overview__compare-table th {
    font-weight: 600;
    background: var(--v-color-surface-muted, #f5f5f5);
}

.v-atlas-raumplanung-feature-info-overview__compare-table-value {
    text-align: right;
    white-space: nowrap;
}

.v-atlas-raumplanung-feature-info-overview__compare-table tr.x-is-main-entity {
    font-weight: 600;
    background: var(--v-color-surface-muted, #f5f5f5);
}
</style>